import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { defineMessages } from 'gatsby-plugin-react-intl';
import { topicTitleMessages, translationKeysBySlug } from './topicAlternateTitles';

const ListStyles = {
  punctuation: 'punctuation',
  conjunction: 'conjunction',
  // TODO: implement more styles like English's last-item-uses-word, 'disjunction' for 'or', ...
};

const messages = defineMessages({
  'prospectus.layout.page.title_format': {
    id: 'prospectus.layout.page.title_format',
    description: 'Page title format, adds suffix about edX offerings',
    defaultMessage: '{title}: Online Courses from Harvard, MIT, Microsoft',
  },
  'prospectus.layout.page.alternate_title_format': {
    id: 'prospectus.layout.page.alternate_title_format',
    description: 'Page title alternate format, title refers to topic',
    defaultMessage: 'Learn {title} with Online {title} Courses',
  },
  'prospectus.formatting.number_range': {
    id: 'prospectus.formatting.number_range',
    description: 'Format a numberical range e.g. 100-150 hours of effort',
    defaultMessage: '{rangeKey, select, '
      + 'range {{low, number}\u2013{high, number}} '
      + 'single {{single, number}} '
      + 'other { }}',
  },
  'prospectus.formatting.list_join': {
    id: 'prospectus.formatting.list_join',
    description: 'Joining mark or word for a list of items, use the {sp} placeholder to include space before the joining word',
    // eslint-disable-next-line prefer-template
    defaultMessage: '{style, select, '
      + 'punctuation {, } ' // HACK: select keys must match ListStyles, above, but must be statically coded for extract
      + 'conjunction { {sp}and } ' // HACK: interpolating a space character to get a leading-space here
      + 'other { }}',
  },
  'prospectus.card.effort_format': {
    id: 'prospectus.card.effort_format',
    description: 'Compose a short description of the time commitment for a course',
    defaultMessage: '{effortType, select, '
      + 'per_for_weeks {{hours} per week, for {weeksToComplete} weeks}'
      + 'per_week {{hours} per week}'
      + 'weeks_only {{weeksToComplete, number} weeks}'
      + 'other {{hours}}}',
  },
});

// Convert and format prices based on currency, conversion rate, and locale
const formatPrice = (price, currency, intl) => {
  const { code = 'USD', rate = 1.0 } = currency;
  let newPrice = parseFloat(price);
  const options = { style: 'currency', currency: code };

  if (Number.isNaN(newPrice)) {
    throw Error(`Price is not a number! price: ${price} - newPrice: ${newPrice}`);
  }

  if (rate !== 1.0) {
    newPrice = Math.round(newPrice * rate);
  }

  if (newPrice % 1 === 0) {
    // Don't include fractional part for whole-numbered prices
    options.minimumFractionDigits = 0; // HACK: Have to set min or max throws out-of-range
    options.maximumFractionDigits = 0;
  }

  newPrice = intl.formatNumber(newPrice, options);
  if (/^[$\d,.\s]+$/.test(newPrice)) {
    // If the formatted price has only potentially ambiguous currency signs ($), digits,
    // separators (,.), and/or spaces, add the code for clarity
    newPrice = `${newPrice} ${code}`;
  }
  return newPrice;
};

// Special formatting for showing a new price as discounted from the original price
//
const formatPricesForComparison = (originalPrice, newPrice, currency, intl) => {
  let formattedPrice = formatPrice(newPrice, currency, intl);
  let formattedOriginalPrice = formatPrice(originalPrice, currency, intl);
  const currencyCodeRE = /\s+[A-Z]{3}$/;
  let priceCurrencyCode = formattedPrice.match(currencyCodeRE) || [];
  if (priceCurrencyCode.length > 0) {
    formattedPrice = formattedPrice.replace(currencyCodeRE, '');
    formattedOriginalPrice = formattedOriginalPrice.replace(currencyCodeRE, '');
    priceCurrencyCode = priceCurrencyCode[0].trim();
  }
  return { formattedPrice, formattedOriginalPrice, priceCurrencyCode };
};

const getI18nDateString = (date, intl, timeZone) => {
  if (date) {
    return intl.formatDate(
      date,
      {
        day: 'numeric', month: 'short', year: 'numeric', timeZone,
      },
    );
  }

  return date;
};

const getI18nDateStringLong = (date, intl, timeZone) => {
  if (date) {
    return intl.formatDate(
      date,
      {
        day: 'numeric', month: 'long', year: 'numeric', timeZone,
      },
    );
  }

  return date;
};

const getI18nDayMonthString = (date, intl) => {
  if (date) {
    return intl.formatDate(
      date,
      { day: 'numeric', month: 'short' },
    );
  }

  return date;
};
const removeHtmlTags = str => str.replace(/(<([^>]+)>)/ig, '');

const replaceUnderscoresWithSpaces = str => str.replace(/_/g, ' ');

const removePunctuationAndSpaces = str => (str ? str.replace(/[^A-Za-z0-9_]/g, '') : '');

const getSubjectDisplayTitle = (title, intl) => intl.formatMessage(messages['prospectus.layout.page.title_format'], { title });

const getTopicDisplayTitle = (title, topicSlug, intl) => {
  const topicAlternativeKey = translationKeysBySlug[topicSlug];
  if (topicAlternativeKey) {
    return intl.formatMessage(topicTitleMessages[topicAlternativeKey]);
  }
  return intl.formatMessage(messages['prospectus.layout.page.alternate_title_format'], { title });
};

const containsLetters = (string) => {
  if (!string) { return false; }
  return /[a-zA-Z\s]+/.test(string.trim());
};

// fields usually undefined, check for corner case nulls
const getInstructorPositionOrg = (position) => {
  const { title, organizationName } = position ?? {};

  const processedTitle = containsLetters(title) ? title : '';
  const processedOrg = containsLetters(organizationName) ? organizationName : '';

  if (processedTitle && processedOrg) {
    return `${processedTitle} • ${processedOrg}`;
  }
  return `${processedTitle}${processedOrg}`;
};

const getFullName = ({ givenName: forename = '', familyName: surname = '' }) => {
  if (forename && surname) {
    return `${forename || ''} ${surname || ''}`;
  }
  return `${forename || ''}${surname || ''}`;
};

const formatSelectKey = key => key.toLowerCase().replace(/ /g, '_');

const formatNumberRange = (low, high, intl) => {
  const single = low || high;
  let rangeKey = 'none';
  if (low && high) {
    rangeKey = 'range';
  } else if (single) {
    rangeKey = 'single';
  }

  return intl.formatMessage(
    messages['prospectus.formatting.number_range'],
    {
      rangeKey, low, high, single,
    },
  );
};

// TODO: plain .join() will not be right for RTL, more logic needed for English too.
const formatList = (items, style, intl) => (
  items.join(intl.formatMessage(
    messages['prospectus.formatting.list_join'],
    { style, sp: ' ' }, // HACK: there isn't a way to escape a leading space in the format, so pass one in
  ))
);

// Formats the amount of effort required for completing a course to display on a card
const formatEffort = (hours, weeksToComplete, intl) => {
  let effortType = 'other';

  if (hours.length > 0 && weeksToComplete > 0) {
    effortType = 'per_for_weeks';
  } else if (hours.length > 0) {
    effortType = 'per_week';
  } else if (!hours && weeksToComplete > 0) {
    effortType = 'weeks_only';
  }

  return intl.formatMessage(
    messages['prospectus.card.effort_format'],
    {
      effortType,
      hours,
      weeksToComplete,
    },
  );
};

// Formats rich text from the CMS Contentful
const formatRichText = (str, options = {}) => {
  if (str) {
    const content = JSON.parse(str);
    return documentToReactComponents(content, options);
  }

  return null;
};

// Rich text comes wrapped in a p by default, and this methods removes it
const removeParagraphsFromRichText = (content) => {
  const options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => children,
    },
  };

  return formatRichText(content, options);
};

const removeWhitespace = (str) => str.replace(/\s+/g, ' ').trim();

const isEmptyHtml = str => (removeHtmlTags(str).trim() === '');

const addParamsToUrl = (url, params, doNotInclude, convertToString) => {
  params.forEach(([_key, _value]) => {
    if (doNotInclude.findIndex((notIncluded) => notIncluded === _key) === -1) {
      url.searchParams.set(_key, _value);
    }
  });
  if (convertToString) {
    return url.toString();
  }
  return url;
};

const addParamsToLeadFormUrl = (leadFormUrl, variant = null, additionalParams = []) => {
  const url = leadFormUrl ? new URL(leadFormUrl) : null;
  const isBrowser = typeof window !== 'undefined';
  let paramsToAdd = [
    ['utm_source', 'edx'],
    ['utm_medium', 'referral'],
    ...additionalParams,
  ];
  if (url) {
    if (variant === 'Boot Camp' && isBrowser) {
      const urlParamsFromLocation = window.location.search;
      const params = new URLSearchParams(urlParamsFromLocation);
      if (typeof window !== 'undefined' && window.location) {
        paramsToAdd.push(['placement_url', `${window.location.protocol}//${window.location.host}${window.location.pathname}`]);
      }
      const current = [...params.entries()];
      paramsToAdd = [...paramsToAdd, ...current];
    }

    return addParamsToUrl(url, paramsToAdd, ['redirect'], true);
  }

  return leadFormUrl;
};

/**
 * Function that get all possible utm params from client url
 *
 * @return {array} return key/value pairs of utm params
 */
const getUTMParamsFromURL = () => {
  const isBrowser = typeof window !== 'undefined';
  if (isBrowser) {
    const urlParamsFromLocation = window.location.search;
    const params = new URLSearchParams(urlParamsFromLocation);
    const currentParams = [...params.entries()];
    const UTMParams = currentParams.filter((param) => param[0].startsWith('utm'));
    if (UTMParams.length !== 0) {
      return UTMParams;
    }
  }
  return [];
};

/**
 * Function for safe attribute string creation
 *
 * @param {string} rawString string to be cleaned and made ready for use as html
 *     attribute value.
 * @return {string} safe string which can be used as html attribute value.
 */
const makeSafeAttributeString = rawString => {
  const regex = /[^a-z0-9_]/g;
  return rawString.toLowerCase().replace("'", '').replace(regex, '-');
};

/**
 * Use case is related to the appearance of " in eaid strings.
 * @param {string} rawString
 * @returns string without any '"'
 */
const formatEaidString = rawString => {
  const regex = /"/g;
  if (typeof rawString === 'string' && rawString.match(regex)) {
    return rawString.replace(regex, '');
  }
  return rawString;
};

const formatProgramPrice = (price) => {
  let formattedNumber = Number(price)?.toFixed(2);

  if (formattedNumber.endsWith('.00')) {
    // eslint-disable-next-line
    formattedNumber = parseInt(price);
  }
  return formattedNumber;
};

/**
 * Clean HTML tags and newlines from string
 * @param {string} string
 * @returns {string} cleaned string
*/
const cleanHTMLString = string => string.replaceAll(/<.*?>/g, '').replaceAll(/(\r\n|\r|\n)/g, '');

/**
 * @param {string} rawHtmlString
 * @returns {Array} array of strings representing each list item
 */
const getListFromRawHTML = rawHtmlString => {
  if (!rawHtmlString) { return []; }
  // Split on beginning of list item and end of list
  const splitString = rawHtmlString.split(/<li>|<\/ul>/g);
  if (splitString.length <= 1) {
    // If there is only one result, that means there are no list items; just return the entire string without the HTML
    // tags as a single item in an array in this case
    return [cleanHTMLString(rawHtmlString)];
  }
  // Otherwise, clean up the list items and return them as an array, ignoring strings outside the list
  return splitString.filter(string => string.includes('</li>')).map(string => cleanHTMLString(string));
};

export {
  containsLetters,
  formatPrice,
  formatPricesForComparison,
  getI18nDateString,
  getI18nDateStringLong,
  getI18nDayMonthString,
  getInstructorPositionOrg,
  getUTMParamsFromURL,
  removeHtmlTags,
  removePunctuationAndSpaces,
  replaceUnderscoresWithSpaces,
  getSubjectDisplayTitle,
  getTopicDisplayTitle,
  formatSelectKey,
  formatNumberRange,
  getFullName,
  ListStyles,
  formatList,
  formatEffort,
  formatRichText,
  removeParagraphsFromRichText,
  removeWhitespace,
  isEmptyHtml,
  addParamsToLeadFormUrl,
  addParamsToUrl,
  makeSafeAttributeString,
  formatEaidString,
  formatProgramPrice,
  cleanHTMLString,
  getListFromRawHTML,
};
